import React, { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFabButton,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { arrowBack, layers, library, createOutline } from 'ionicons/icons';
import annotationIcon from '../../../icons/annotation.svg';
import { MappedAreaModel } from '../models/mapped-area-model';
import { AnnotationModel } from '../models/annotation.model';
import { AttributeModel } from '../models/attribute.model';
import { CreationAnnotationViewWithoutLocation, UpdateAnnotationViewWithoutLocation } from '../types/annotation.types';
import { EditIonBackButton } from '../../shared/components/edit-ion-back-button.component';
import { PlanPresenter } from '../presenters/plan.presenter';
import { Map } from './map.component';
import { Attributes } from './attributes.component';
import { AnnotationForm } from './annotation-form.component';
import { useAnnotations } from '../hooks/use-annotations.hook';
import { useAttributes } from '../hooks/use-attributes.hook';
import { useMappedArea } from '../hooks/use-mapped-area.hook';
import { UpdateAttributeViewWithoutLocation } from '../types/attribute.type';
import { EquipmentCharacteristics } from './equipment-characteristics.component';
import { EquipmentCharacteristicsPresenterFactory } from '../presenters/equipment-characteristics.presenter';
import { EquipmentCharacteristicsModel } from '../models/equipment-characteristics.model';
import { useAnnotationIdContext } from '../contexts/annotation-id.context';

import './plan.component.scss';

interface PlanComponentProps {
  planId: string;
  backRoute: string;
  getAttributes: (assetId: string) => Promise<AttributeModel[]>;
  updateAttribute: (
    componentId: string,
    attributeId: string,
    updateAttribute: UpdateAttributeViewWithoutLocation
  ) => Promise<Response>;
  getPlan: () => Promise<MappedAreaModel>;
  getAnnotation: () => Promise<AnnotationModel[]>;
  createAnnotation: (annotation: CreationAnnotationViewWithoutLocation) => Promise<Response>;
  updateAnnotation: (annotationId: string, annotation: UpdateAnnotationViewWithoutLocation) => Promise<Response>;
  deleteAnnotation: (annotationId: string, annotationFileName: string | null) => Promise<Response>;
  getCharacteristics: (componentTypeCodes: string[]) => Promise<EquipmentCharacteristicsModel[]>;
  buildingAddress?: string;
}

export const PlanComponent: FunctionComponent<PlanComponentProps> = ({
                                                                       planId,
                                                                       backRoute,
                                                                       getAttributes,
                                                                       updateAttribute,
                                                                       getPlan,
                                                                       getAnnotation,
                                                                       createAnnotation,
                                                                       updateAnnotation,
                                                                       deleteAnnotation,
                                                                       getCharacteristics,
                                                                       buildingAddress,
                                                                     }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'MAP' | 'ATTRIBUTES'>('MAP');
  const [zoomCoordinates, setZoomCoordinates] = useState<{x: number, y: number} | null>(null);
  const [lastZoomedAnnotationId, setLastZoomedAnnotationId] = useState<string | null>(null);
  const [clickedElementType, setClickedElementType] = useState<string>('');

  const { mappedArea } = useMappedArea(getPlan);

  const {
    openFormForAnnotationCreation,
    createAnnotationOnPosition,
    openFormForAnnotationUpdate,
    updateAnnotationThenCloseForm,
    deleteAnnotationThenCloseAndEmptyForm,
    closeAndEmptyAnnotationForm,
    saveClickedPosition,
    annotationToVisualize,
    isAnnotationFormVisible,
    setAnnotationFormVisibility,
    annotationPositionName,
    currentFilterStatusValue,
    setCurrentFilterStatusValue,
    annotations
  } = useAnnotations(planId, getAnnotation, createAnnotation, updateAnnotation, deleteAnnotation);

  const {
    attributesForEquipmentIdSelected,
    isAttributesLoading,
    equipmentIdSelected,
    selectEquipmentId,
    attributeName,
    setAttributeName,
    onAttributeValueChanged,
    onCharacteristicChanged,
    selectedEquipmentCharacteristics,
    selectedEquipmentCharacteristicsInitialValues,
    equipmentAttributeNameSelected,
  } = useAttributes(getAttributes, updateAttribute, getCharacteristics);

  const { annotationIdFromList, setAnnotationIdFromList } = useAnnotationIdContext();

  const clearZoomCoordinates = () => {
    setZoomCoordinates(null);
  };

  // Réinitialiser l'annotation sélectionnée quand on accède au plan sans passer par le détail d'une annotation
  useEffect(() => {
    // Si on a un annotationIdFromList au montage, c'est qu'on vient d'une annotation
    // Sinon, on réinitialise pour éviter de garder une annotation précédente
    if (!annotationIdFromList) {
      setLastZoomedAnnotationId(null);
      setZoomCoordinates(null);
    }
  }, [annotationIdFromList]); // Dépendance ajoutée

  useEffect(() => {
    if (
      annotationIdFromList &&
      mappedArea &&
      annotations.length > 0 &&
      annotationIdFromList !== lastZoomedAnnotationId // On ne zoom que si c'est une nouvelle annotation
    ) {
      const selectedAnnotation = annotations.find(annotation => annotation.id === annotationIdFromList);
      if (selectedAnnotation) {
        setZoomCoordinates({ x: selectedAnnotation.x, y: selectedAnnotation.y });
        setLastZoomedAnnotationId(annotationIdFromList); // On mémorise l'ID zoomé
        // Réinitialiser l'annotationIdFromList après avoir effectué le zoom
        setAnnotationIdFromList(null);
      }
    }
  }, [annotationIdFromList, mappedArea, annotations, lastZoomedAnnotationId, setAnnotationIdFromList]);

  return (
    <IonPage>
      <IonHeader role="header">
        <IonToolbar>
          <IonButtons slot="start">
            { activeTab === 'MAP' ? (
              <EditIonBackButton defaultHref={ backRoute }/>
            ) : (
              <IonButton className={ 'back-to-plan' } onClick={ () => setActiveTab('MAP') }>
                <IonIcon slot="icon-only" icon={ arrowBack } size={ 'large' }/>
              </IonButton>
            ) }
          </IonButtons>
          { activeTab === 'MAP' && <IonTitle>{ mappedArea?.name ?? t('plan.title') }</IonTitle> }
          { activeTab === 'ATTRIBUTES' && <IonTitle>{ attributeName }</IonTitle> }
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={ activeTab === 'ATTRIBUTES' } scrollX={ false }>
        <div data-testid="map" role="main" style={ { display: activeTab === 'MAP' ? 'block' : 'none', height: '90%' } }>{
          <>
          <div style={ { marginLeft: 16, marginRight: 16 } }>
            { buildingAddress && (
              <IonLabel style={{ 
                fontSize: '10px',
                display: 'block',
                fontFamily: 'Open Sans, sans-serif',
                fontWeight: 400,
                lineHeight: '1',
                letterSpacing: '0.5px',
                color: '#165BE6',
                marginBottom: '5px',
                marginTop: '16px'
              }} color={ 'tertiary' }>
                { buildingAddress }
              </IonLabel>
            )}
            <IonItem lines="full">
              <IonSelect
                label={t('plan.annotations.filter.status.label')}
                labelPlacement='stacked'
                value={ currentFilterStatusValue }
                multiple={ true }
                onIonChange={ (event) =>
                  setCurrentFilterStatusValue(event.detail.value)
                }
                cancelText={ t('plan.annotations.filter.status.CANCEL_BUTTON') }
                okText={ t('plan.annotations.filter.status.SAVE_BUTTON') }
              >
                <IonSelectOption value={ 'NEW' }>
                  { t('plan.annotations.filter.status.NEW') }
                </IonSelectOption>
                <IonSelectOption value={ 'PENDING' }>
                    { t('plan.annotations.filter.status.PENDING') }
                </IonSelectOption>
                <IonSelectOption value={ 'TODO' }>
                  { t('plan.annotations.filter.status.TODO') }
                </IonSelectOption>
                <IonSelectOption value={ 'IN_PROGRESS' }>
                  { t('plan.annotations.filter.status.IN_PROGRESS') }
                </IonSelectOption>
                <IonSelectOption value={ 'DONE' }>
                  { t('plan.annotations.filter.status.DONE') }
                </IonSelectOption>
                <IonSelectOption value={ 'ARCHIVED' }>
                  { t('plan.annotations.filter.status.ARCHIVED') }
                </IonSelectOption>
              </IonSelect>
            </IonItem>
          </div>
          <Map
            planSVG={ mappedArea ? new PlanPresenter(mappedArea) : null }
            planId={ planId }
            annotations={ annotations }
            onCreateAnnotation={ openFormForAnnotationCreation }
            onEditAnnotation={ openFormForAnnotationUpdate }
            saveClickedEquipmentId={ selectEquipmentId }
            saveClickedPosition={ saveClickedPosition }
            saveClickName={ setAttributeName }
            saveClickedElementType={ setClickedElementType }
            equipmentIdSelected={ equipmentIdSelected }
            zoomCoordinates={ zoomCoordinates }
            onZoomComplete={ clearZoomCoordinates }
          />
          </>
        }
        </div>

        <div role="complementary" style={ { display: activeTab === 'ATTRIBUTES' ? 'block' : 'none' } }>
          <Attributes
            isPlanExported={mappedArea?.isExported ?? false}
            attributes={ attributesForEquipmentIdSelected }
            isAttributesLoading={ isAttributesLoading }
            onAttributeValueChanged={ onAttributeValueChanged }
            buildingAddress={ buildingAddress }
          />
          {
            (selectedEquipmentCharacteristics != null && selectedEquipmentCharacteristicsInitialValues != null)
            &&
            <EquipmentCharacteristics
              equipmentCharacteristics={
                EquipmentCharacteristicsPresenterFactory(selectedEquipmentCharacteristics, selectedEquipmentCharacteristicsInitialValues)
              }
              onSelectValue={ (value: string, label: string, type:  'LINE'|'COLUMN') =>
                onCharacteristicChanged(attributesForEquipmentIdSelected[0].componentId, equipmentAttributeNameSelected,value,label,type) }
            />
          }
        </div>
      </IonContent>

      <IonModal isOpen={ isAnnotationFormVisible }>
        { isAnnotationFormVisible && (
          <AnnotationForm
            isPlanExported={mappedArea?.isExported ?? false}
            annotationToVisualize={ annotationToVisualize }
            createAnnotation={ createAnnotationOnPosition }
            updateAnnotation={ updateAnnotationThenCloseForm }
            deleteAnnotation={ deleteAnnotationThenCloseAndEmptyForm }
            closePopover={ closeAndEmptyAnnotationForm }
            annotationPositionName={ annotationPositionName }
            clickedElementType={ clickedElementType }
          />
        ) }
      </IonModal>

      { activeTab === 'ATTRIBUTES' && (
        <IonFab vertical="bottom" horizontal="center" slot="fixed">
          <IonFabButton color={ 'secondary' } onClick={ () => setAnnotationFormVisibility(true) }>
            <img src={ annotationIcon } alt="" style={ { height: '30px' } }/>
          </IonFabButton>
        </IonFab>
      ) }

      <IonFooter slot="bottom">
        <IonToolbar>
          <IonButtons slot="start" className="ion-padding">
            <IonButton
              role="button"
              color={ activeTab === 'MAP' ? 'secondary' : 'medium' }
              onClick={ () => setActiveTab('MAP') }
            >
              { t('plan.tabs.map') }
              <IonIcon slot="end" icon={ layers }/>
            </IonButton>
          </IonButtons>

          <IonButtons slot="end" className="ion-padding">
            <IonButton
              role="button"
              disabled={ equipmentIdSelected === '' || undefined }
              color={ activeTab === 'ATTRIBUTES' ? 'secondary' : 'medium' }
              onClick={ () => setActiveTab('ATTRIBUTES') }
            >
              <IonIcon slot="start" icon={ createOutline } style={{ fontSize: '16px', marginRight: '4px' }}/>
              { t('plan.tabs.attributes') }
              <IonIcon slot="end" icon={ library }/>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};
